import DatePicker from '@vtex/styleguide/lib/DatePicker'
import { useIntl } from '@/checkout-instore-legacy/hooks/intl'
import React, { useState, useRef, useEffect } from 'react'

import { Flex, csx } from '@vtex/admin-ui'
import {
  FilterAttribute,
  FilterDateRange,
  FilterValue,
} from '@/checkout-instore-legacy/components/FilterSidebar/typings'

type DatePickerStateProps = {
  startDate: null | Date
  endDate: null | Date
}

type DatePickerRangeProps = {
  attribute: FilterAttribute
  onChange: (key: string, value: any) => void
  values: FilterDateRange[] | FilterValue[]
}

export const DatePickerRange = ({
  attribute,
  onChange,
  values,
}: DatePickerRangeProps) => {
  const intl = useIntl()

  const initialState: DatePickerStateProps = {
    startDate: null,
    endDate: null,
  }

  const [dateValues, setDateValues] = useState(initialState)
  const valueDate = useRef(initialState)

  useEffect(() => {
    const startDate = values.find(
      (value) => (value as FilterDateRange).key === 'startAt'
    ) as FilterDateRange
    const endDate = values.find(
      (value) => (value as FilterDateRange).key === 'endAt'
    ) as FilterDateRange

    const newDateValues: DatePickerStateProps = {
      startDate: startDate?.date ? new Date(startDate.date) : null,
      endDate: endDate?.date ? new Date(endDate.date) : null,
    }
    setDateValues(newDateValues)
  }, [values])

  const isEndDateValid = (value: Date) => {
    if (dateValues.endDate === null) return true

    return dateValues.endDate >= value
  }

  const handleDatePickerChange = (dateRef: 'start' | 'end', value: Date) => {
    setDateValues({
      ...dateValues,
      startDate: dateRef === 'start' ? value : dateValues.startDate,
      endDate:
        dateRef === 'end'
          ? value
          : isEndDateValid(value)
          ? dateValues.endDate
          : null,
    })

    valueDate.current = {
      ...valueDate.current,
      startDate: dateRef === 'start' ? value : dateValues.startDate,
      endDate:
        dateRef === 'end'
          ? value
          : isEndDateValid(value)
          ? dateValues.endDate
          : null,
    }

    onChange(attribute.key, [
      {
        key: 'startAt',
        date: valueDate.current.startDate,
        active: !!valueDate.current.startDate,
      },
      {
        key: 'endAt',
        date: valueDate.current.endDate,
        active: !!valueDate.current.endDate,
      },
    ])
  }

  return (
    <Flex direction="column" className={csx({ width: 'full' })}>
      <div className={csx({ marginBottom: '$space-3' })}>
        <DatePicker
          size="regular"
          placeholder={intl.formatMessage({ id: 'daterange.from' })}
          value={dateValues.startDate}
          onChange={(date: Date) => handleDatePickerChange('start', date)}
          locale={intl.locale}
        />
      </div>
      <DatePicker
        size="regular"
        placeholder={intl.formatMessage({ id: 'daterange.to' })}
        value={dateValues.endDate}
        onChange={(date: Date) => handleDatePickerChange('end', date)}
        locale={intl.locale}
        minDate={dateValues.startDate}
      />
    </Flex>
  )
}
